import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { Shield, Clock, Truck, ArrowRight } from 'lucide-react';
import RelatedCategories from '../../components/catalog/RelatedCategories';
import ContactForm from '../../components/forms/ContactForm';

const products = [
  {
    id: 'vvg',
    title: 'Кабель ВВГ',
    description: 'Силовой кабель с медными жилами и ПВХ изоляцией',
    features: [
      'Сечение от 1,5 до 240 мм²',
      'Количество жил: 1-5',
      'Напряжение: 0,66 и 1 кВ',
      'Температура: -50°C до +50°C',
    ],
    image: '/images/transformers/kable.JPG',
  },
  {
    id: 'avbbshv',
    title: 'Кабель АВБбШв',
    description: 'Силовой кабель с алюминиевыми жилами в броне',
    features: [
      'Сечение от 2,5 до 240 мм²',
      'Количество жил: 1-5',
      'Напряжение: 0,66 и 1 кВ',
      'Броня из стальных лент',
    ],
    image: '/images/transformers/kable.JPG',
  },
  {
    id: 'kvvg',
    title: 'Кабель КВВГ',
    description: 'Контрольный кабель с медными жилами',
    features: [
      'Сечение от 0,75 до 6 мм²',
      'Количество жил: 4-37',
      'Напряжение: 0,66 кВ',
      'Для систем контроля',
    ],
    image: '/images/transformers/kable.JPG',
  },
  {
    id: 'spe',
    title: 'Кабель с изоляцией из СПЭ',
    description: 'Силовой кабель с изоляцией из сшитого полиэтилена',
    features: [
      'Сечение от 35 до 800 мм²',
      'Напряжение: до 35 кВ',
      'Повышенная пропускная способность',
      'Длительный срок службы',
    ],
    image: '/images/transformers/kable.JPG',
  },
];

const CablePage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Силовые кабели - поставка и продажа | Проминнова</title>
        <meta
          name="description"
          content="Поставка силовых и контрольных кабелей различных марок. Медные и алюминиевые кабели для промышленных объектов. Доставка по России."
        />
      </Helmet>

      <div className="bg-primary-50 py-12">
        <div className="container-custom">
          <Breadcrumbs />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Силовые кабели</h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Поставка силовых и контрольных кабелей для промышленных и гражданских объектов
          </p>
        </div>
      </div>

      <div className="container-custom py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-2xl font-bold mb-6">О кабельной продукции</h2>
            <p className="text-lg text-gray-600 mb-8">
              Мы поставляем широкий ассортимент кабельной продукции для различных областей
              применения: от силовых кабелей для промышленных объектов до контрольных кабелей для
              систем автоматизации.
            </p>
            <div className="space-y-6">
              <div className="flex items-start">
                <Shield className="w-5 h-5 text-primary-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-medium mb-1">Гарантия качества</h3>
                  <p className="text-gray-600">
                    Вся продукция сертифицирована и соответствует ГОСТ
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <Clock className="w-5 h-5 text-primary-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-medium mb-1">Сроки поставки</h3>
                  <p className="text-gray-600">От 1 до 5 дней при наличии на складе</p>
                </div>
              </div>
              <div className="flex items-start">
                <Truck className="w-5 h-5 text-primary-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-medium mb-1">Доставка по России</h3>
                  <p className="text-gray-600">Организуем доставку в любой регион</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-xl font-semibold mb-6">Получить расчет стоимости</h3>
            <ContactForm />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {products.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="h-64 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="341"
                  height="256"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">{product.title}</h3>
                <p className="text-gray-600 mb-4">{product.description}</p>
                <ul className="space-y-2 mb-6">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-600">
                      <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/catalog/cable/${product.id}`}
                  className="inline-flex items-center text-primary-600 font-medium hover:text-primary-700"
                >
                  Подробнее
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        <RelatedCategories currentCategory="cable" />

        <div className="prose prose-lg max-w-none">
          <h2>Кабельная продукция для любых задач</h2>
          <p>
            Компания «Проминнова» предлагает широкий ассортимент кабельной продукции от ведущих
            производителей. Мы поставляем кабели различного назначения для промышленных предприятий,
            строительных компаний и электромонтажных организаций.
          </p>

          <h3>Преимущества работы с нами:</h3>
          <ul>
            <li>Большой складской запас популярных марок кабеля</li>
            <li>Прямые поставки от производителей</li>
            <li>Конкурентные цены</li>
            <li>Оперативная отгрузка</li>
            <li>Резка кабеля под заказ</li>
            <li>Техническая поддержка</li>
          </ul>

          <p>
            Вся поставляемая продукция имеет необходимые сертификаты качества и соответствует
            требованиям ГОСТ. Мы обеспечиваем комплексные поставки кабельной продукции для
            реализации проектов любой сложности.
          </p>
        </div>
      </div>
    </>
  );
};

export default CablePage;
